import { isAdmin, isManager } from './userTypes';
import {
  REPORTING_LEVEL_DIRECT,
  PERMISSION_VIEW_AND_EDIT_CUSTOM,
  PERMISSION_VIEW_AND_EDIT,
  PERMISSION_VIEW_ONLY_CUSTOM,
  PERMISSION_VIEW_ONLY,
} from './constants';

export const hasAnyAccessPermission = (value) => {
  return (
    value === PERMISSION_VIEW_AND_EDIT ||
    value === PERMISSION_VIEW_ONLY ||
    value === PERMISSION_VIEW_ONLY_CUSTOM ||
    value === PERMISSION_VIEW_AND_EDIT_CUSTOM
  );
};

export const isEmployeeManagedByUser = (user, employeeId) => {
  return Boolean(
    user.userRole.reportingLevel === REPORTING_LEVEL_DIRECT
      ? user.managedUsersIds?.includes(employeeId)
      : user.indirectReportsIds?.includes(employeeId),
  );
};

export const isUserAdminOfEmployeePermissionLevels = (user, employee) => {
  if (isManager(user)) {
    return false;
  }
  return Boolean(
    (user.userRole?.locationPermissionLevel?.includes('all') ||
      user.userRole?.locationPermissionLevel?.includes(String(employee?.locationId))) &&
      (user.userRole?.teamPermissionLevel?.includes('all') ||
        user.userRole?.teamPermissionLevel?.includes(String(employee?.teamId))) &&
      (user.userRole?.statusPermissionLevel?.includes('all') ||
        user.userRole?.statusPermissionLevel?.includes(employee?.employeeType)),
  );
};

export const canAdminStartRoleChange = (user, employee) => {
  if (!user?.userRole || !employee || isManager(user)) return false;

  const { locationPermissionLevel, teamPermissionLevel, statusPermissionLevel } = user.userRole;
  const { locationId, location, teamId, team, employeeType } = employee;

  const locationSet = new Set(locationPermissionLevel);
  const teamSet = new Set(teamPermissionLevel);
  const statusSet = new Set(statusPermissionLevel);

  return (
    ['all', String(locationId), String(location?.id)].some((id) => locationSet.has(id)) &&
    ['all', String(teamId), String(team?.id)].some((id) => teamSet.has(id)) &&
    ['all', employeeType].some((type) => statusSet.has(type))
  );
};

export const isUserManagerOfEmployee = (user, employee) => {
  const isDirectManger = user.id === employee?.manager?.id;
  return Boolean(
    user.userRole.reportingLevel === REPORTING_LEVEL_DIRECT
      ? isDirectManger && employee.manager.managedUsersIds?.includes(employee.id)
      : isDirectManger && employee.manager.indirectReportsIds?.includes(employee.id),
  );
};

const hasEmployeeRelationship = (user, employee, relationshipType) =>
  isManager(user) &&
  user[relationshipType]?.length > 0 &&
  (isEmployeeManagedByUser(user, employee.id) || isUserManagerOfEmployee(user, employee));

export const isIndirectManagerOfEmployee = (user, employee) =>
  hasEmployeeRelationship(user, employee, 'indirectReportsIds');

export const isManagerOfEmployee = (user, employee) => hasEmployeeRelationship(user, employee, 'managedUsersIds');

export const isAnyManagerOfEmployee = (user, employee) =>
  isIndirectManagerOfEmployee(user, employee) || isManagerOfEmployee(user, employee);

export const isAdminOfEmployee = (user, employee) =>
  isAdmin(user) && isUserAdminOfEmployeePermissionLevels(user, employee);

export const canViewOtherUser = (user, employee) =>
  isManagerOfEmployee(user, employee) || isAdminOfEmployee(user, employee);

export const isViewingSelf = (user, employee) => employee === null || user.id === employee?.id;

export const visibilityCheckBasedOnReportingLevel = (user, employee) => {
  return Boolean(
    user.userRole.reportingLevel === REPORTING_LEVEL_DIRECT
      ? user.id === employee.id // only able to see direct reports (own team's) all information
      : isManagerOfEmployee(user, employee) || isIndirectManagerOfEmployee(user, employee),
  );
};
